import { PropsWithChildren, useMemo, useState } from 'react';
import createSafeContext from '@paradigm/utils/src/createSafeContext';
import { POSITIONS_GROUPS, PositionGroup } from '#/positions/entities/groups';
import { PositionView } from '../entities/positions';

interface PositionsGroupState {
  readonly selectedGroups: string[];
  readonly toggleGroup: (groupName: PositionGroup['value']) => void;
  readonly isGroupActive: (groupName: PositionGroup['value']) => boolean;
}

const [usePositionGroups, PositionGroupsProviderCtx] =
  createSafeContext<PositionsGroupState>('PositionsGroups');

export { usePositionGroups };

const INITIAL_STATE: Set<PositionGroup['value']> = new Set(['product']);

export default function PositionGroupsProvider({
  children,
}: PropsWithChildren) {
  const [selectedGroups, setSelectedGroups] =
    useState<Set<PositionGroup['value']>>(INITIAL_STATE);

  const value: PositionsGroupState = useMemo(
    () => ({
      selectedGroups: [...selectedGroups.values()].sort(sortGroupsByListOrder),
      toggleGroup: (groupName) => {
        if (selectedGroups.has(groupName)) {
          selectedGroups.delete(groupName);
        } else {
          selectedGroups.add(groupName);
        }
        setSelectedGroups(new Set(selectedGroups));
      },
      isGroupActive: (groupName) => selectedGroups.has(groupName),
    }),
    [selectedGroups],
  );
  return (
    <PositionGroupsProviderCtx value={value}>
      {children}
    </PositionGroupsProviderCtx>
  );
}

function sortGroupsByListOrder(
  a: PositionGroup['value'],
  b: PositionGroup['value'],
) {
  const groupIds = POSITIONS_GROUPS.map((g) => g.value);
  return groupIds.indexOf(a) - groupIds.indexOf(b);
}

const hasOnlyOne = (
  positions: readonly PositionView[],
  key: keyof PositionView,
) => new Set(positions.map((p) => p[key])).size === 1;

export const hasSameAccount = (positions: readonly PositionView[]) =>
  hasOnlyOne(positions, 'accountName');

export const hasSameProduct = (positions: readonly PositionView[]) =>
  hasOnlyOne(positions, 'productCode');
